import React from "react";
import { Button, Form, FormInstance, InputNumber } from "antd";
import { GET_ORGANIZERS_CURSOR } from "graphql/query/organizer-gql";
import Localize from "components/service/localize";
import { FormElements } from "components/layout";
import { IFilter } from "../../helpers/flooring-variation-types-helper";

interface IFilterList
{
  [ key: string ]: IFilter;
}

interface IFlooringVariationFiltersFormProps
{
  setCount: React.Dispatch<any>;
  setFilters: React.Dispatch<React.SetStateAction<IFilter | undefined>>;
  setFilterList: React.Dispatch<React.SetStateAction<IFilterList | undefined>>;
  setResetSorting: React.Dispatch<React.SetStateAction<{ column: string; order: "ASC" | "DESC" }[] | undefined>>;
  filterList: IFilterList | undefined;
  flooringVariationFilters: IFilterList | undefined;
  filtersStorageKey: string;
  sortingStorageKey: string;
  objOrderBy: { column: string; order: "ASC" | "DESC" }[];
  action: () => void;
  gqlBuilderWhere: (filters: any) => any;
  form: FormInstance;
}

const hasValue = (value: string | number | null | undefined) =>
  value !== null && value !== undefined && value !== "";

const getMinInitialValue = (
  filters: IFilterList | undefined,
  column: string
) =>
{
  const filter = filters?.[ column ];

  if (filter?.operator === "GTE" || filter?.operator === "BETWEEN") {
    return filter.value[ 0 ];
  }

  return undefined;
};

const getMaxInitialValue = (
  filters: IFilterList | undefined,
  column: string
) =>
{
  const filter = filters?.[ column ];

  if (filter?.operator === "LTE") {
    return filter.value[ 0 ];
  }

  if (filter?.operator === "BETWEEN") {
    return filter.value[ 1 ];
  }

  return undefined;
};

const FlooringVariationFiltersForm: React.FC<IFlooringVariationFiltersFormProps> = ({
  form,
  action,
  setCount,
  setFilters,
  setFilterList,
  setResetSorting,
  filterList,
  flooringVariationFilters,
  filtersStorageKey,
  sortingStorageKey,
  objOrderBy,
  gqlBuilderWhere,
}) =>
{
  const updateFilterList = (nextFilterList: IFilterList) =>
  {
    setFilterList(nextFilterList);
  };

  const onChangeSetFilter = (
    e: React.ChangeEvent<HTMLInputElement> | false,
    name: string,
    value: string | string[] | number | null
  ) =>
  {
    if (e) {
      name = e.target.name;
      value = e.target.value;
    }

    const nextFilterList = {
      ...flooringVariationFilters,
      ...filterList,
    };

    if (hasValue(Array.isArray(value) ? value[ 0 ] : value)) {
      nextFilterList[ name ] = {
        column: name,
        operator: "EQ",
        value: Array.isArray(value) ? value : [ value as string | number ],
      };
    } else {
      delete nextFilterList[ name ];
    }

    updateFilterList(nextFilterList);
  };

  const onChangeSetRangeFilter = (
    column: "PRICE" | "PRICE_BASIC",
    minField: string,
    maxField: string
  ) =>
  {
    const minValue = form.getFieldValue(minField);
    const maxValue = form.getFieldValue(maxField);
    const nextFilterList = {
      ...flooringVariationFilters,
      ...filterList,
    };

    if (hasValue(minValue) && hasValue(maxValue)) {
      nextFilterList[ column ] = {
        column,
        operator: "BETWEEN",
        value: [ minValue, maxValue ],
      };
    } else if (hasValue(minValue)) {
      nextFilterList[ column ] = {
        column,
        operator: "GTE",
        value: [ minValue ],
      };
    } else if (hasValue(maxValue)) {
      nextFilterList[ column ] = {
        column,
        operator: "LTE",
        value: [ maxValue ],
      };
    } else {
      delete nextFilterList[ column ];
    }

    updateFilterList(nextFilterList);
  };

  const resetFilters = () =>
  {
    setCount({});
    setFilterList({});
    setFilters(gqlBuilderWhere({}));
    setResetSorting(objOrderBy);
    localStorage.removeItem(filtersStorageKey);
    localStorage.removeItem(sortingStorageKey);
  };

  return (
    <div className="ant-form ant-form-vertical filters-form">
      <div>
        <FormElements.RadioSelect
          skipQuery={!flooringVariationFilters}
          location={true}
          form={form}
          query={GET_ORGANIZERS_CURSOR}
          model="organizersCursor"
          name="ORGANIZER_ID"
          label="FORMS.Input_Label_Organizer"
          modalTitle="FORMS.Title_SelectOrganizer"
          initialValue={
            flooringVariationFilters?.ORGANIZER_ID?.value
              ? [
                {
                  value: flooringVariationFilters.ORGANIZER_ID.value[ 0 ].toString(),
                },
              ]
              : undefined
          }
          onChangeSetFilter={() =>
            onChangeSetFilter(false, "ORGANIZER_ID", form.getFieldValue("ORGANIZER_ID"))}
        />

        <Form.Item
          name="MIN_PRICE"
          label={<Localize>FORMS.Input_Label_MinWholePrice</Localize>}
          initialValue={getMinInitialValue(flooringVariationFilters, "PRICE")}
        >
          <InputNumber
            name="MIN_PRICE"
            min={0}
            style={{ width: "100%" }}
            onChange={() => onChangeSetRangeFilter("PRICE", "MIN_PRICE", "MAX_PRICE")}
          />
        </Form.Item>

        <Form.Item
          name="MAX_PRICE"
          label={<Localize>FORMS.Input_Label_MaxWholePrice</Localize>}
          initialValue={getMaxInitialValue(flooringVariationFilters, "PRICE")}
        >
          <InputNumber
            name="MAX_PRICE"
            min={0}
            style={{ width: "100%" }}
            onChange={() => onChangeSetRangeFilter("PRICE", "MIN_PRICE", "MAX_PRICE")}
          />
        </Form.Item>

        <Form.Item
          name="MIN_PRICE_BASIC"
          label={<Localize>FORMS.Input_Label_MinBasicFlooringPrice</Localize>}
          initialValue={getMinInitialValue(flooringVariationFilters, "PRICE_BASIC")}
        >
          <InputNumber
            name="MIN_PRICE_BASIC"
            min={0}
            style={{ width: "100%" }}
            onChange={() =>
              onChangeSetRangeFilter(
                "PRICE_BASIC",
                "MIN_PRICE_BASIC",
                "MAX_PRICE_BASIC"
              )}
          />
        </Form.Item>

        <Form.Item
          name="MAX_PRICE_BASIC"
          label={<Localize>FORMS.Input_Label_MaxBasicFlooringPrice</Localize>}
          initialValue={getMaxInitialValue(flooringVariationFilters, "PRICE_BASIC")}
        >
          <InputNumber
            name="MAX_PRICE_BASIC"
            min={0}
            style={{ width: "100%" }}
            onChange={() =>
              onChangeSetRangeFilter(
                "PRICE_BASIC",
                "MIN_PRICE_BASIC",
                "MAX_PRICE_BASIC"
              )}
          />
        </Form.Item>
      </div>

      <div className="form-btn-holder">
        <Button
          className="light-bg"
          htmlType="submit"
          onClick={resetFilters}
        >
          <Localize>FORMS.Button_Text_ResetFilters</Localize>
        </Button>

        <Button
          className="btn-right"
          type="primary"
          htmlType="submit"
          onClick={() =>
          {
            const nextFilterList = filterList ?? flooringVariationFilters ?? {};

            setFilters(gqlBuilderWhere(nextFilterList));
            setCount(nextFilterList);
            action();
          }}
        >
          <Localize>FORMS.Button_Text_ApplyFilters</Localize>
        </Button>
      </div>
    </div>
  );
};

export default FlooringVariationFiltersForm;
